import type { McpServerDto } from '@dbrain/shared';
import { Alert, Badge, Button, Card, EmptyState, Field, TextInput } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { capKeys, useServers } from '../../api/capabilities.js';
import { useMe } from '../../api/hooks.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { Microsoft365Overview } from '../../components/microsoft365/admin/OverviewCard.js';
import { FirmAdminOnly } from '../../components/microsoft365/admin/parts.js';
import { Microsoft365Setup } from '../../components/microsoft365/admin/SetupWizard.js';
import { Microsoft365Summary } from '../../components/microsoft365/admin/Summary.js';
import { Microsoft365TeamAdmin } from '../../components/microsoft365/admin/TeamAdminView.js';
import { AdminLayout } from './AdminLayout.js';
import { isOrganizationAdmin } from './sections.js';

/**
 * A sign-in client registered beforehand (Phase 11 open question 2): for a
 * service that does not register clients itself, the admin enters the
 * client id and secret once; the secret is never shown again.
 */
function OauthClientForm({ server }: { server: McpServerDto }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState(server.oauthClient?.clientId ?? '');
  const [secret, setSecret] = useState('');
  const done = () => void queryClient.invalidateQueries({ queryKey: capKeys.serversAll });
  const save = useMutation({
    mutationFn: () => api('PUT', `/api/mcp/servers/${server.id}/oauth-client`, { clientId: clientId.trim(), ...(secret ? { clientSecret: secret } : {}) }),
    onSuccess: () => {
      setSecret('');
      done();
    },
  });
  const clear = useMutation({ mutationFn: () => api('DELETE', `/api/mcp/servers/${server.id}/oauth-client`), onSuccess: done });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      data-testid={`oauth-client-${server.name}`}
    >
      <p className="muted">
        {server.oauthClient
          ? t('integrations.oauth.saved', { clientId: server.oauthClient.clientId ?? '' })
          : t('integrations.oauth.none')}{' '}
        {server.oauthClient?.hasSecret ? <Badge>{t('integrations.oauth.secretSaved')}</Badge> : null}
      </p>
      <ErrorAlert error={save.error ?? clear.error} />
      {save.isSuccess ? <Alert tone="success" title={t('integrations.oauth.done')} /> : null}
      <div className="row">
        <Field label={t('integrations.oauth.clientId')}>
          {({ id }) => <TextInput id={id} value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="oauth-client-id" />}
        </Field>
        <Field label={t('integrations.oauth.secret')} hint={t('integrations.oauth.secretHint')}>
          {({ id, describedBy }) => (
            <TextInput id={id} aria-describedby={describedBy} type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="oauth-client-secret" />
          )}
        </Field>
      </div>
      <div className="row">
        <Button type="submit" size="sm" busy={save.isPending} disabled={!clientId.trim()} data-testid="oauth-client-save">
          {t('common.save')}
        </Button>
        {server.oauthClient ? (
          <Button size="sm" variant="ghost" busy={clear.isPending} onClick={() => clear.mutate()}>
            {t('integrations.oauth.clear')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** The firm's connections for everyone (Herkes), with their sign-in clients: organization admins only. */
function TenantCatalog({ titled }: { titled: boolean }) {
  const { t } = useTranslation();
  const servers = useServers();
  const catalog = (servers.data ?? []).filter((s) => s.scope === 'tenant');
  return (
    <section className="stack" data-testid="integrations-catalog">
      {titled ? <h2 className="section-head__title">{t('microsoft365.admin.overview.catalog')}</h2> : null}
      <p className="muted">
        {t('integrations.hint')} <Link to="/connectors">{t('integrations.manage')}</Link>
      </p>
      <ErrorAlert error={servers.error} />
      {catalog.length === 0 ? <EmptyState>{t('integrations.empty')}</EmptyState> : null}
      <div className="grid">
        {catalog.map((server) => (
          <Card
            key={server.id}
            title={
              <span className="row">
                {server.displayName} {server.catalogMode ? <Badge>{t(`integrations.modes.${server.catalogMode}`)}</Badge> : null}
              </span>
            }
            data-testid={`integration-${server.name}`}
          >
            <p className="muted mono">{server.url ?? server.command}</p>
            <p>
              {t(`integrations.auth.${server.authKind}`)} · {t('integrations.tools', { count: server.tools.length })}
            </p>
            {server.authKind === 'oauth' ? <OauthClientForm server={server} /> : null}
          </Card>
        ))}
      </div>
    </section>
  );
}

/**
 * Yönetim › Entegrasyonlar. Organization admins: in a firm, "Hazır bağlantılar" (Microsoft 365, Phase 15e) above
 * the firm's own connections; in a personal space the connections alone. Team admins (D-101 as amended in 15e):
 * only Microsoft 365's switch for the teams they lead, never the connections or the setup.
 */
export function IntegrationsPage() {
  const { t } = useTranslation();
  const me = useMe().data;
  const firm = me?.tenant.kind === 'organization';
  return (
    <AdminLayout section="integrations" title={t('admin.sections.integrations')}>
      {!me ? null : isOrganizationAdmin(me) ? (
        <div className="stack integrations-page">
          {firm ? <Microsoft365Overview /> : null}
          <TenantCatalog titled={firm} />
        </div>
      ) : (
        <Microsoft365TeamAdmin />
      )}
    </AdminLayout>
  );
}

/** Yönetim › Entegrasyonlar › Microsoft 365: the summary (a firm's organization admins). */
export function Microsoft365Page() {
  const { t } = useTranslation();
  return (
    <AdminLayout section="integrations" title={t('admin.sections.integrations')}>
      <FirmAdminOnly>
        <Microsoft365Summary />
      </FirmAdminOnly>
    </AdminLayout>
  );
}

/** Yönetim › Entegrasyonlar › Microsoft 365 › Kur: the four steps (a firm's organization admins; never team admins). */
export function Microsoft365SetupPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout section="integrations" title={t('admin.sections.integrations')}>
      <FirmAdminOnly>
        <Microsoft365Setup />
      </FirmAdminOnly>
    </AdminLayout>
  );
}
